import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import {
  BottomActionArea,
  BottomActionButton,
  FormHeader,
  NavigateBeforeButton,
  FormBody,
  FormContentContainer,
} from "../../../components";
import { FormContainer } from "../../../styles/styled";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiGateway } from "@contexts";
import { Question, QuestionsChallengeActionError } from "../../../interfaces";
import {
  ErrorType,
  onQuestionChallengeAxiosErrorInterpreter,
} from "../hooks/onQuestionChallengeError";
import { useEnrolmentStatusContext } from "@contexts/EnrolmentStatusContext";

interface Props {
  questions?: Question[];
  onError: (error: Error) => Promise<void>;
}

interface Answer {
  questionId: string;
  answerId: string;
}

const PaddedRadioGroup = styled(RadioGroup)({
  paddingInline: "16px",
});

export const QuestionsChallengeForm: React.FC<Props> = ({
  questions,
  onError,
}) => {
  const { getEnrolmentStatus } = useEnrolmentStatusContext();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
  } = useForm({ mode: "onChange" });

  const { mutate: validateChallenge, isPending } = useMutation({
    mutationFn: (answers: Answer[]) =>
      apiGateway<void, ErrorType>(
        {
          method: "post",
          url: "/enrolment/questions-challenge/verify",
          data: { answers },
        },
        onQuestionChallengeAxiosErrorInterpreter,
      ),
    onSuccess: async () => {
      await getEnrolmentStatus();
    },
    onError: async (err) => {
      if (err instanceof QuestionsChallengeActionError) {
        if (err.action === "invalidInput") reset();
        await onError(err);
        return;
      }
      reset();
      await onError(err);
    },
  });

  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    return navigate("/app/solicitudes");
  }, [navigate]);

  const onSubmit = async (data: Record<string, string>) => {
    const answers: Answer[] = Object.entries(data).map(([key, value]) => ({
      questionId: key,
      answerId: value,
    }));
    validateChallenge(answers);
  };
  return (
    <>
      <FormHeader
        title="Verificación de identidad"
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Typography>Responde estas preguntas de seguridad</Typography>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            {questions?.map(({ questionId, questionText, options }) => (
              <div key={questionId}>
                <Typography fontWeight={500} marginBottom={"16px"}>
                  {questionText}
                </Typography>
                <Controller
                  control={control}
                  name={`${questionId}`}
                  defaultValue=""
                  rules={{ required: true }}
                  render={({ field }) => (
                    <PaddedRadioGroup {...field}>
                      {options.map(({ optionId, optionText }) => (
                        <FormControlLabel
                          key={`${questionId}-${optionId}`}
                          value={optionId}
                          control={<Radio size="small" />}
                          label={optionText}
                        />
                      ))}
                    </PaddedRadioGroup>
                  )}
                />
              </div>
            ))}
            <BottomActionArea>
              <BottomActionButton
                type="submit"
                disabled={!isValid || isPending}
              >
                {isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Siguiente"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
